import { useEffect, useState, type ReactNode } from "react";
import { Send, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

export interface SubmitBillReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submitting: boolean;
  onConfirm: () => void;
  /** Points the user at the approval-policies page; the integrator owns the route. */
  settingsLink?: ReactNode;
  /** A fuzzy supplier match auto-accepted on submit; when set, submit needs an explicit acknowledgement. */
  unconfirmedSupplierName?: string;
  /** Confidence (0-100) of the unconfirmed supplier suggestion, for display. */
  unconfirmedSupplierConfidence?: number;
}

/** Routing is decided server-side by the matching approval policy, so there is no reviewer preview here. */
export function SubmitBillReviewDialog({
  open,
  onOpenChange,
  submitting,
  onConfirm,
  settingsLink,
  unconfirmedSupplierName,
  unconfirmedSupplierConfidence,
}: SubmitBillReviewDialogProps) {
  const gated = !!unconfirmedSupplierName;
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit Bill for Review</DialogTitle>
          <DialogDescription>
            Your approval policies will decide what happens next - the bill may be
            auto-approved, routed to an approver, or auto-voided.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <p className="text-sm text-muted-foreground">
            The applied rule and who the bill is routed to will be shown on the bill
            once it&apos;s submitted.
            {settingsLink ? <> Manage rules in {settingsLink}.</> : null}
          </p>
        </div>

        {gated && (
          <label className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
            <Checkbox
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="flex items-center gap-1.5 font-medium text-amber-700">
                <TriangleAlert className="size-3.5" />
                Unconfirmed supplier match
              </span>
              <span className="mt-1 block text-muted-foreground">
                We&apos;ll set the supplier to{" "}
                <span className="font-medium text-foreground">{unconfirmedSupplierName}</span>
                {unconfirmedSupplierConfidence != null
                  ? ` (${unconfirmedSupplierConfidence}% match)`
                  : ""}
                . Tick to confirm it&apos;s correct.
              </span>
            </span>
          </label>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || (gated && !acknowledged)}
          >
            {submitting ? <Spinner className="size-4" /> : <Send className="size-4" />}
            Confirm &amp; Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
